// src/lib/entityCache.js
//
// Generic in-memory LRU cache for entities, keyed by (type, id).
// Designed as the Stage 1 backbone for offline-ish UX — hooks read from here
// first and render synchronously, then refetch in the background to refresh.
//
// Built around three operations:
//   get(type, key)                        — returns cached value or undefined
//   set(type, key, value)                 — stores, evicts LRU if full, notifies subscribers
//   subscribe(type, key, callback)        — re-render hook when that entry changes
//
// Per-type caps live in TYPE_LIMITS below. "bodies" holds several thousand
// email bodies; "threads" is smaller; "entity:*" will be populated by Stage 1b
// for deals/projects/contacts/etc.
//
// Not persisted to IndexedDB yet — that's Stage 2. This is pure in-memory, but
// the API is stable so Stage 2 can swap the storage layer without touching
// call sites.

const TYPE_LIMITS = {
  body:   2000,   // ~2000 email bodies @ ~3KB avg = ~6MB
  thread: 500,    // ~500 threads, each with N messages
  // list: paginated list results from ~14 module hooks (inbox, deals,
  // projects, contacts, invoices, tasks, …). Each hook can have multiple
  // filter permutations cached concurrently. Bumped from 50 → 200 now that
  // queryCache writes through here — needed budget for every module's
  // common filter sets to survive without evicting each other.
  // Bumped from 200 → 500: inbox polling creates 50-100 list entries per session
  // which was evicting contacts/deals/projects entries from the LRU during IDB
  // hydration at boot, leaving modules cache-cold on every fresh page load.
  list:   500,
  entity: 5000,   // generic bucket for deals/projects/contacts/etc (Stage 1b)
}

// Per-type LRU: Map<key, value> preserves insertion order; re-insertion on
// access moves the entry to the end so eviction drops the oldest.
const stores = {}  // { [type]: Map<key, value> }

// Listeners: Map<`${type}:${key}`, Set<callback>>
const listeners = new Map()

// Optional write-through to persistent storage (IndexedDB via persistentCache).
// Registered post-hydration so hydration itself doesn't re-emit back to disk.
let _persistentWriter = null

export function _registerPersistentWriter(fn) {
  _persistentWriter = fn
}

function getStore(type) {
  if (!stores[type]) stores[type] = new Map()
  return stores[type]
}

function notify(type, key) {
  const hook = `${type}:${key}`
  const set = listeners.get(hook)
  if (!set) return
  for (const cb of set) {
    try { cb() } catch (e) { console.warn('[entityCache] listener failed:', e.message) }
  }
}

export function get(type, key) {
  if (key == null) return undefined
  const store = getStore(type)
  if (!store.has(key)) return undefined
  // Touch for LRU: re-insert so it's now the most-recently-used.
  const value = store.get(key)
  store.delete(key)
  store.set(key, value)
  return value
}

export function set(type, key, value) {
  if (key == null) return
  const store = getStore(type)
  if (store.has(key)) store.delete(key)
  store.set(key, value)
  const limit = TYPE_LIMITS[type] || 1000
  while (store.size > limit) {
    const oldest = store.keys().next().value
    store.delete(oldest)
  }
  notify(type, key)
  if (_persistentWriter) {
    try { _persistentWriter(type, key, value) }
    catch (e) { console.warn('[entityCache] persistent writer failed:', e.message) }
  }
}

export function has(type, key) {
  if (key == null) return false
  return getStore(type).has(key)
}

export function remove(type, key) {
  if (key == null) return
  getStore(type).delete(key)
  notify(type, key)
}

// Clear every entry of one TYPE bucket (e.g. 'list' or 'entity') — a bounded
// invalidation, narrower than wiping the whole cache, for a caller that knows
// exactly which bucket a stale read lives in (the demo scenario-content
// version check in src/lib/demoCacheReset.js is the first user: a reseed that
// moves the primary scenario invalidates business-list/entity reads, never
// email bodies/threads, which that reseed never touches). Notifies every
// mounted subscriber so a hook currently rendering stale data re-fetches.
export function clearType(type) {
  const store = getStore(type)
  const keys = [...store.keys()]
  store.clear()
  for (const key of keys) notify(type, key)
}

// Clear only the entries of one TYPE bucket whose key matches `predicate` —
// narrower still than clearType(), for a caller that knows exactly which
// KEYS are affected (not every key the type bucket happens to hold). First
// caller: src/lib/demoCacheReset.js, which must invalidate the specific
// business-list query-cache prefixes ('properties:', 'projects:', 'deals:',
// 'tasks:', 'dashboard:due-today:') and the specific entity ids a scenario
// retirement/reseed touches — a blanket clearType('list')/clearType('entity')
// would also throw away an unrelated module's cached contacts/companies/
// inbox reads for no reason tied to the demo content change. Returns the
// removed keys so a caller can log/verify what was actually dropped.
export function clearMatching(type, predicate) {
  const store = getStore(type)
  const removed = []
  for (const key of [...store.keys()]) {
    if (predicate(key)) { store.delete(key); removed.push(key) }
  }
  for (const key of removed) notify(type, key)
  return removed
}

export function subscribe(type, key, callback) {
  if (key == null || typeof callback !== 'function') return () => {}
  const hook = `${type}:${key}`
  if (!listeners.has(hook)) listeners.set(hook, new Set())
  listeners.get(hook).add(callback)
  return () => {
    const set = listeners.get(hook)
    if (!set) return
    set.delete(callback)
    if (set.size === 0) listeners.delete(hook)
  }
}

// Batch set — useful for prefetch / list hydration. Fires one notify per key.
export function setMany(type, entries) {
  for (const [key, value] of entries) set(type, key, value)
}

// ─── Entity helpers ─────────────────────────────────────────────────────────
// Convenience wrappers around the generic get/set for the "entity" bucket —
// used by Stage 1b (deals / projects / contacts / etc caching by primary key).
// Each entity type gets its own sub-namespace ("entity:deals", "entity:projects")
// so a deal with id X doesn't collide with a project with the same id. We route
// through the generic store under one type key ("entity") for LRU-budget reasons,
// but the cache key prefixes the sub-type.

const entityKey = (entityType, id) => `${entityType}:${id}`

export function getEntity(entityType, id) {
  return get('entity', entityKey(entityType, id))
}

export function setEntity(entityType, id, value) {
  set('entity', entityKey(entityType, id), value)
}

export function hasEntity(entityType, id) {
  return has('entity', entityKey(entityType, id))
}

export function removeEntity(entityType, id) {
  remove('entity', entityKey(entityType, id))
}

// Seed the cache from a list response. idField defaults to 'id'.
// Returns the seeded count — useful to log / confirm.
export function seedEntityList(entityType, items, idField = 'id') {
  if (!Array.isArray(items)) return 0
  let n = 0
  for (const item of items) {
    const id = item && item[idField]
    if (id) { setEntity(entityType, id, item); n++ }
  }
  return n
}

// Subscribe to changes on a specific entity — re-render hook.
export function subscribeEntity(entityType, id, callback) {
  return subscribe('entity', entityKey(entityType, id), callback)
}

// Return all [key, value] pairs for a given type — used by voiceContext to
// search cached list results without touching IndexedDB.
export function entries(type) {
  const store = getStore(type)
  return [...store.entries()]
}

// Dev / debugging helper: expose current sizes.
export function stats() {
  const out = {}
  for (const [type, store] of Object.entries(stores)) {
    out[type] = { size: store.size, limit: TYPE_LIMITS[type] || 1000 }
  }
  return out
}

if (typeof window !== 'undefined') {
  // Expose for console debugging during dev — no PII, just counts.
  window.__entityCacheStats = stats
}
